import React, { useState, useRef } from "react";
import { FaTimes } from "react-icons/fa";

const StaffFilterPanel = ({ isOpen, onClose, onApplyFilters, mode = 'temp', initialSortBy, initialSortDirection, initialSelectedTypes, focusField }) => {
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [typeDropdownOpen, setTypeDropdownOpen] = useState(false);
  const [minProximity, setMinProximity] = useState("");
  const [maxProximity, setMaxProximity] = useState("");
  const [ignoreAvailability, setIgnoreAvailability] = useState(false);
  const [sortBy, setSortBy] = useState("Preference");
  const [sortDirection, setSortDirection] = useState("Ascending");
  const typeSectionRef = useRef(null);
  const sortBySectionRef = useRef(null);

  // Sync initial sort values when panel mode or provided initials change
  React.useEffect(() => {
    if (mode === 'outsourced') {
      setSortBy(initialSortBy || 'Outsourced Agency');
      setSortDirection(initialSortDirection || 'Ascending');
    } else {
      setSortBy(initialSortBy || 'Preference');
      setSortDirection(initialSortDirection || 'Ascending');
    }
  }, [mode, initialSortBy, initialSortDirection]);

  // Sync selected types whenever the panel opens or initialSelectedTypes changes
  React.useEffect(() => {
    if (isOpen) {
      setSelectedTypes(Array.isArray(initialSelectedTypes) ? initialSelectedTypes : []);
    }
  }, [isOpen, initialSelectedTypes]);

  // When focusField is set, scroll to the relevant section
  React.useEffect(() => {
    if (!isOpen) return;
    if (focusField === 'type') {
      setTypeDropdownOpen(true);
      setTimeout(() => {
        typeSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 50);
    } else if (focusField === 'sortBy') {
      setTimeout(() => {
        sortBySectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        sortBySectionRef.current?.querySelector('select')?.focus();
      }, 50);
    }
  }, [isOpen, focusField]);

  

  const staffTypes = ["Candidate", "Field Staff", "Outsourced Staff"];
  const sortOptions = ["Preference", "Proximity", "Utilization", "Staff First Name", "Staff Last Name"];
  const sortDirections = ["Ascending", "Descending"];

  const handleTypeToggle = (type) => {
    if (selectedTypes.includes(type)) {
      setSelectedTypes(selectedTypes.filter(t => t !== type));
    } else {
      setSelectedTypes([...selectedTypes, type]);
    }
  };

  const outsourcedSortOptions = ["Outsourced Agency"];

  const handleClear = () => {
    if (mode === 'outsourced') {
      setSortBy(outsourcedSortOptions[0]);
      setSortDirection('Ascending');
      return;
    }
    setSelectedTypes([]);
    setTypeDropdownOpen(false);
    setMinProximity("");
    setMaxProximity("");
    setIgnoreAvailability(false);
    setSortBy("Preference");
    setSortDirection("Ascending");
  };

  const handleDone = () => {
    const filters = {
      types: selectedTypes,
      minProximity,
      maxProximity,
      ignoreAvailability,
      sortBy,
      sortDirection
    };
    onApplyFilters(filters);
    onClose();
  };

  // Close dropdown when clicking outside
  const handleBackdropClick = (e) => {
    setTypeDropdownOpen(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-30 z-40"
        onClick={handleBackdropClick}
      />
      
      {/* Filter Panel */}
      <div className="fixed top-0 right-0 h-full w-80 bg-white shadow-lg z-50 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-3 border-b border-gray-200">
          <h3 className="text-base font-medium text-gray-900">Filters</h3>
          <button 
            className="p-1 hover:bg-gray-100 rounded"
            onClick={onClose}
          >
            <FaTimes size={16} className="text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div 
          className="flex-1 overflow-y-auto p-3 space-y-4"
          onClick={(e) => {
            // Close dropdown if clicking outside the type dropdown area
            if (!e.target.closest('.type-dropdown-container')) {
              setTypeDropdownOpen(false);
            }
          }}
        >
          {mode === 'outsourced' ? (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Sort By</label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-red-500 focus:border-red-500"
                >
                  {outsourcedSortOptions.map((option) => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Sort Direction</label>
                <select
                  value={sortDirection}
                  onChange={(e) => setSortDirection(e.target.value)}
                  className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-red-500 focus:border-red-500"
                >
                  {sortDirections.map((direction) => (
                    <option key={direction} value={direction}>{direction}</option>
                  ))}
                </select>
              </div>
            </>
          ) : (
            <>
              
              {/* Type Filter */}
              <div ref={typeSectionRef}>
                <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
                <div className="relative type-dropdown-container">
                  <input
                    type="text"
                    readOnly
                    value={selectedTypes.length > 0 ? selectedTypes.join(', ') : 'Select types...'}
                    onClick={() => setTypeDropdownOpen(!typeDropdownOpen)}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-red-500 focus:border-red-500 cursor-pointer"
                  />
                  <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none">
                    <svg className={`w-4 h-4 text-gray-400 transform transition-transform ${typeDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                  {typeDropdownOpen && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded shadow-lg">
                      <div className="py-2">
                        {staffTypes.map((type) => (
                          <label key={type} className="flex items-center px-2 py-1.5 cursor-pointer hover:bg-gray-50">
                            <input
                              type="checkbox"
                              checked={selectedTypes.includes(type)}
                              onChange={() => handleTypeToggle(type)}
                              className="w-3 h-3 text-red-600 border-gray-300 rounded focus:ring-red-500"
                            />
                            <span className="ml-2 text-xs text-gray-700">{type}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Ignore Availability Checkbox */}
              <div>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={ignoreAvailability}
                    onChange={(e) => setIgnoreAvailability(e.target.checked)}
                    className="w-3 h-3 text-red-600 border-gray-300 rounded focus:ring-red-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Ignore availability & requirements</span>
                </label>
              </div>

              {/* Sort By */}
              <div ref={sortBySectionRef} className={focusField === 'sortBy' ? 'ring-2 ring-red-400 rounded p-1 -m-1' : ''}>
                <label className="block text-sm font-medium text-gray-700 mb-2">Sort By</label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-red-500 focus:border-red-500"
                >
                  {sortOptions.map((option) => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>

              {/* Sort Direction */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Sort Direction</label>
                <select
                  value={sortDirection}
                  onChange={(e) => setSortDirection(e.target.value)}
                  className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-red-500 focus:border-red-500"
                >
                  {sortDirections.map((direction) => (
                    <option key={direction} value={direction}>{direction}</option>
                  ))}
                </select>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-3 border-t border-gray-200 bg-gray-50">
          <div className="flex gap-2">
            <button 
              onClick={handleClear}
              className="flex-1 px-3 py-1.5 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 text-xs"
            >
              CLEAR
            </button>
            <button 
              onClick={handleDone}
              className="flex-1 px-3 py-1.5 bg-red-600 text-white rounded hover:bg-red-700 text-xs"
            >
              DONE
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default StaffFilterPanel;